/**
 * Shared building blocks for the D3 core resources (projects, tasks, artifacts,
 * documents). Co-located under `projects/` (owned by Frontend D3) and imported
 * by the sibling resource folders. Keeps list loading/error/empty states and a
 * server-driven DataTable consistent across the four resources without coupling
 * to the (concurrently authored) refine-ui wrapper components — it relies only
 * on canonical shadcn primitives under `@/components/ui/*`.
 */
import * as React from "react";
import type { ColumnDef, Table as TanstackTable } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import type { CrudSort } from "@refinedev/core";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  EmptyState,
  ErrorState,
  TableSkeletonRows,
  TableStateRow,
} from "@/components/ui/states";
import { PageHeader as SharedPageHeader } from "@/components/layout/PageHeader";

/** Minimal shape of the `refineCore` object returned by `useTable`. */
export interface RefineTableCore {
  current: number;
  setCurrent: (value: number) => void;
  pageCount: number;
  pageSize: number;
  sorters: CrudSort[];
  setSorters: (sorters: CrudSort[]) => void;
  tableQuery: {
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
    data?: { total?: number } | undefined;
  };
}

/**
 * Canonical EMPTY / LOADING / ERROR state trio (design-system §7). The single
 * implementation lives in `@/components/ui/states` (Skeleton-based loading, the
 * shadcn `Empty` primitive, a calm inline error). Re-exported here so the ~10
 * existing importers (`../projects/_shared`) keep their import paths and prop
 * shapes unchanged — there is now ONE state vocabulary across the app.
 */
export {
  LoadingState,
  EmptyState,
  ErrorState,
  type LoadingStateProps,
  type EmptyStateProps,
  type ErrorStateProps,
} from "@/components/ui/states";

/** Debounced free-text search box wired to the list `q` filter. */
export function SearchBox({
  value,
  onChange,
  placeholder = "Search…",
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative w-full max-w-sm">
      <Icon
        name="search"
        size={16}
        className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground"
        aria-hidden
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-8"
        aria-label="Search"
      />
    </div>
  );
}

/**
 * Server-driven DataTable. Reads loading/error/empty from the Refine core query
 * and renders sortable headers + pagination controls. Sorting and pagination are
 * delegated back to Refine (server-side) — nothing is sorted/paged in-browser.
 */
export function DataTable<TData extends { id: string }>({
  table,
  core,
  columns,
  emptyMessage,
  onRowClick,
}: {
  table: TanstackTable<TData>;
  core: RefineTableCore;
  columns: ColumnDef<TData, unknown>[];
  emptyMessage?: string;
  onRowClick?: (row: TData) => void;
}) {
  const { tableQuery } = core;
  const rows = table.getRowModel().rows;
  const sortFor = (field: string): "asc" | "desc" | undefined =>
    core.sorters.find((s) => s.field === field)?.order;

  const toggleSort = (field: string) => {
    const currentOrder = sortFor(field);
    const nextOrder: "asc" | "desc" = currentOrder === "asc" ? "desc" : "asc";
    core.setSorters([{ field, order: nextOrder }]);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const colId = header.column.id;
                  const sortable = header.column.columnDef.enableSorting !== false;
                  const order = sortFor(colId);
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : sortable ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(colId)}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {order === "asc" ? (
                            <Icon name="sortAsc" size={12} aria-hidden />
                          ) : order === "desc" ? (
                            <Icon name="sortDesc" size={12} aria-hidden />
                          ) : null}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {tableQuery.isLoading ? (
              <TableSkeletonRows columns={columns.length} />
            ) : tableQuery.isError ? (
              <TableStateRow columns={columns.length}>
                <ErrorState error={tableQuery.error} />
              </TableStateRow>
            ) : rows.length === 0 ? (
              <TableStateRow columns={columns.length}>
                <EmptyState message={emptyMessage} />
              </TableStateRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={onRowClick ? "cursor-pointer" : undefined}
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      onClick={(e) => {
                        // Let interactive cells (action buttons/links) handle
                        // their own clicks without triggering row navigation.
                        if (cell.column.id === "actions") e.stopPropagation();
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination core={core} />
    </div>
  );
}

/** Prev/next pagination footer driven by the Refine core page state. */
function Pagination({ core }: { core: RefineTableCore }) {
  const total = core.tableQuery.data?.total ?? 0;
  const pageCount = Math.max(core.pageCount, 1);
  const from = total === 0 ? 0 : (core.current - 1) * core.pageSize + 1;
  const to = Math.min(core.current * core.pageSize, total);

  return (
    <div className="flex items-center justify-between text-sm text-muted-foreground">
      <span>
        {total === 0 ? "0 results" : `${from}–${to} of ${total}`}
      </span>
      <div className="flex items-center gap-2">
        <span>
          Page {core.current} of {pageCount}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={core.current <= 1 || core.tableQuery.isFetching}
          onClick={() => core.setCurrent(core.current - 1)}
          aria-label="Previous page"
        >
          <Icon name="prev" size={16} aria-hidden />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={core.current >= pageCount || core.tableQuery.isFetching}
          onClick={() => core.setCurrent(core.current + 1)}
          aria-label="Next page"
        >
          <Icon name="next" size={16} aria-hidden />
        </Button>
      </div>
    </div>
  );
}

/** Hook: keep a debounced `q` filter in sync with the Refine table. */
export function useSearchFilter(
  setFilters: (
    filters: { field: string; operator: "contains"; value: string }[],
  ) => void,
) {
  const [value, setValue] = React.useState("");
  React.useEffect(() => {
    const handle = setTimeout(() => {
      setFilters(
        value
          ? [{ field: "q", operator: "contains", value }]
          : [{ field: "q", operator: "contains", value: "" }],
      );
    }, 300);
    return () => clearTimeout(handle);
  }, [value, setFilters]);
  return [value, setValue] as const;
}

/**
 * Resource-list page header (feedback #6). Re-exports the canonical shared
 * `components/layout/PageHeader`, keeping the legacy `description`/`action` prop
 * names as aliases for `subtitle`/`actions` so the ~35 resource lists and the
 * ListScaffold keep compiling unchanged while every page renders ONE header
 * structure + type ramp. Optional `icon` (an `IconName`) passes straight through.
 */
export function PageHeader({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: IconName;
}) {
  return (
    <SharedPageHeader
      title={title}
      subtitle={description}
      actions={action}
      icon={icon}
    />
  );
}
